// src/api/creditoEmpresa.js
import { apiFetch } from './index.js';

/**
 * Obtener resumen de créditos unificado (Compras, Ventas, Reparaciones, Servicios Externos)
 * @returns {Promise} Resumen de créditos
 */
export const obtenerResumenCreditos = async () => {
    try {
        const response = await apiFetch('/CreditoEmpresa/resumen');
        return {
            success: true,
            data: response.data || response,
            message: 'Resumen de créditos obtenido exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo resumen de créditos:', error);
        return {
            success: false,
            error: error.message,
            data: { compras: [], ventas: [], reparaciones: [], serviciosExternos: [] }
        };
    }
};

/**
 * Registrar un pago de compra
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoCompra = async (pago) => {
    try {
        const response = await apiFetch('/PagoCompra', {
            method: 'POST',
            body: pago
        });
        return {
            success: true,
            data: response.data || response,
            message: 'Pago registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de compra:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Registrar un pago de reparación (Salida Equipo)
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoReparacion = async (pago) => {
    try {
        // 1. Registrar el pago a nivel global de la salida
        const response = await apiFetch('/PagoSalidaEquipo', {
            method: 'POST',
            body: pago
        });

        // 2. Si viene de una cuota específica (Cashea), registrar el pago de esa cuota
        if (pago.idCuota) {
            await apiFetch('/CreditoSalidaEquipo/pagar', {
                method: 'POST',
                body: {
                    idCreditoSalidaEquipo: pago.idCuota,
                    montoPagoUsd: pago.montoUsd,
                    montoPagoBs: pago.montoBs || 0,
                    fechaPago: pago.fechaPago || new Date().toISOString(),
                    observaciones: pago.referencia || "Pago de cuota"
                }
            });
        }

        return {
            success: true,
            data: response.data || response,
            message: 'Pago registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de reparación:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Registrar un pago de Servicio Externo
 * @param {Object} pago - Datos del pago
 * @returns {Promise} Resultado del registro
 */
export const registrarPagoServicioExterno = async (pago) => {
    try {
        const response = await apiFetch('/PagoServicioExterno', {
            method: 'POST',
            body: {
                idServicioExterno: pago.idServicioExterno,
                montoUsd: pago.montoUsd,
                montoBs: pago.montoBs || 0,
                metodoPago: pago.metodoPago || 'Efectivo',
                referencia: pago.referencia || '',
                idUsuario: pago.idUsuario || 1,
                observaciones: pago.observaciones || ''
            }
        });

        // Si es un crédito con cuotas, actualizar el estado
        if (pago.idCuota) {
            await apiFetch('/CreditoServicioExterno/pagar', {
                method: 'POST',
                body: {
                    idCreditoServicioExterno: pago.idCuota,
                    montoPagoUsd: pago.montoUsd,
                    montoPagoBs: pago.montoBs || 0,
                    fechaPago: pago.fechaPago || new Date().toISOString(),
                    observaciones: pago.referencia || "Pago de cuota"
                }
            });
        }

        return {
            success: true,
            data: response.data || response,
            message: 'Pago de servicio externo registrado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error registrando pago de servicio externo:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * Obtener pagos realizados a una compra específica
 * @param {number} idCompra - ID de la compra
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosCompra = async (idCompra) => {
    try {
        const response = await apiFetch(`/PagoCompra/compra/${idCompra}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Pagos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo pagos de compra:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Obtener pagos realizados a un servicio externo específico
 * @param {number} idServicioExterno - ID del servicio externo
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosServicioExterno = async (idServicioExterno) => {
    try {
        const response = await apiFetch(`/PagoServicioExterno/servicio/${idServicioExterno}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Pagos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo pagos de servicio externo:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Obtener créditos de servicios externos por cliente
 * @param {number} idCliente - ID del cliente
 * @returns {Promise} Lista de créditos
 */
export const obtenerCreditosServicioExternoPorCliente = async (idCliente) => {
    try {
        const response = await apiFetch(`/CreditoServicioExterno/cliente/${idCliente}`);
        return {
            success: true,
            data: response.data || response,
            message: 'Créditos obtenidos exitosamente'
        };
    } catch (error) {
        console.error('❌ Error obteniendo créditos de servicio externo:', error);
        return {
            success: false,
            error: error.message,
            data: []
        };
    }
};

/**
 * Crear crédito para un servicio externo
 * @param {Object} credito - Datos del crédito
 * @returns {Promise} Resultado del registro
 */
export const crearCreditoServicioExterno = async (credito) => {
    try {
        const response = await apiFetch('/CreditoServicioExterno', {
            method: 'POST',
            body: {
                idServicioExterno: credito.idServicioExterno,
                idCliente: credito.idCliente,
                montoTotal: credito.montoTotal,
                montoPagado: credito.montoPagado || 0,
                saldoPendiente: credito.saldoPendiente || credito.montoTotal,
                fechaVencimiento: credito.fechaVencimiento || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                estado: credito.estado || 'PENDIENTE',
                observaciones: credito.observaciones || '',
                tipoCredito: credito.tipoCredito || 'EMPRESA',
                tasaInteres: credito.tasaInteres || 0,
                numeroCuotas: credito.numeroCuotas || 1
            }
        });
        return {
            success: true,
            data: response.data || response,
            message: 'Crédito creado exitosamente'
        };
    } catch (error) {
        console.error('❌ Error creando crédito de servicio externo:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// EXPORTACIÓN POR DEFECTO (para compatibilidad)
export default {
    obtenerResumenCreditos,
    registrarPagoCompra,
    registrarPagoReparacion,
    registrarPagoServicioExterno,
    obtenerPagosCompra,
    obtenerPagosServicioExterno,
    obtenerCreditosServicioExternoPorCliente,
    crearCreditoServicioExterno
};